import React from 'react'
import { useEffect, useState } from 'react'
import { APIFunctions, getUserTypeFunctions } from '../../lib/apiFunctions'
import { ComplianceReportsTypes } from '../../lib/enums'
import { IPeriodCache } from '../../lib/interfaces'
import { User } from '../../types/session'
import RevisorNav from '../Revisor/RevisorNav'
import LLUserComplianceReports from './LLUserComplianceReports'
import ReportMeta from './ReportMeta'
import ReportView from './ReportView'
import TaxYearOverview from './TaxYearOverview'
import { ITaxYear, ITaxYearData, ITaxYearDataRes } from './types'

export default function UseTaxYears({
    contentType,
    getUserData,
}: {
    contentType: ComplianceReportsTypes
    getUserData: (LLUserId: string) => Promise<User | null>
}) {
    const [isFetchingTaxYearsData, setIsFetchingTaxYearsData] = useState<{
        [taxYearId: string]: boolean
    }>({})
    const [taxYearsData, setTaxYearsData] = useState<{
        [taxYearId: string]: ITaxYearDataRes
    }>({})
    const [taxYears, setTaxYears] = useState<ITaxYear[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [didLoad, setDidLoad] = useState<boolean>(false)
    const [isBackgroundLoading, setIsBackgroundLoading] = useState<boolean>(false)
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false)
    const [selectedKey, setSelectedKey] = useState<string>('create')
    const [userFunctions, setUserFunctions] = useState<APIFunctions | null>(null)
    const [userData, setUserData] = useState<User | null>(null)

    useEffect(() => {
        const [view, viewId] = selectedKey.split(';;;')
        let taxYear: ITaxYear | undefined
        switch (view) {
            case 'taxYear':
                taxYear = taxYears.find((ty) => ty.id === viewId)
                break
            case 'taxYearReport':
                const taxYearId = viewId.split(':::')[0]
                taxYear = taxYears.find((ty) => ty.id === taxYearId)
                break
        }
        if (!taxYear) return
        getUserData(taxYear.ll_user_id)
            .then((data) => {
                setUserData(data)
            })
            .catch(() => {})
    }, [selectedKey, taxYears, getUserData])

    useEffect(() => {
        setUserFunctions(getUserTypeFunctions(contentType))
    }, [contentType])

    async function getTaxYearData(
        taxYear: ITaxYear,
        forceFetch = false
    ): Promise<ITaxYearDataRes | undefined> {
        if (!taxYear) {
            return
        }
        if (isFetchingTaxYearsData[taxYear.id]) return
        if (!forceFetch && Object.keys(taxYearsData).includes(taxYear.id)) {
            return taxYearsData[taxYear.id]
        } else {
            if (!userFunctions) return
            setIsFetchingTaxYearsData({
                ...isFetchingTaxYearsData,
                [taxYear.id]: true,
            })
            const data = await userFunctions.fetchTaxYearData(taxYear)
            const newCRD = { ...taxYearsData }
            newCRD[taxYear.id] = data
            setTaxYearsData(newCRD)
            setIsFetchingTaxYearsData({
                ...isFetchingTaxYearsData,
                [taxYear.id]: false,
            })
            return data
        }
    }

    async function refreshReportDatesData(reportId: string): Promise<void> {
        setIsBackgroundLoading(true)
        setIsRefreshing(true)
        const taxYearIds = Object.keys(taxYearsData)
        for (var i = 0; i < taxYearIds.length; i++) {
            const tyId = taxYearIds[i]
            const report: ITaxYearData | undefined = taxYearsData[tyId].tax_year_reports.find(
                (r) => r.id === reportId
            )
            if (report && !report.report_meta.is_locked && userFunctions) {
                const newData = await userFunctions.getReportData(report.id)
                const newCRD = { ...taxYearsData }
                newCRD[tyId] = {
                    tax_year_reports: taxYearsData[tyId].tax_year_reports.map((rep) => {
                        if (rep.id !== reportId) return rep
                        return {
                            ...rep,
                            report_data: {
                                ...newData.data,
                                dates: newData.data.dates,
                            },
                        }
                    }),
                }
                setTaxYearsData(newCRD)
                setIsBackgroundLoading(false)
                setIsRefreshing(false)
            }
        }
    }

    async function resetTaxYearData(taxYear: ITaxYear) {
        if (!taxYear) {
            return
        }
        delete taxYearsData[taxYear.id]
        await loadData()
    }

    async function loadData(postSwitchToTab: string | null = null): Promise<ITaxYear[]> {
        setIsLoading(true)
        setDidLoad(true)
        const TMPAPIFunctions = userFunctions ?? getUserTypeFunctions(contentType)
        const taxYearsDataRes = await TMPAPIFunctions.getTaxYears()
        if (taxYearsDataRes?.tax_years) setTaxYears(taxYearsDataRes?.tax_years)
        if (postSwitchToTab) setSelectedKey(postSwitchToTab)
        setIsLoading(false)
        return taxYearsDataRes?.tax_years
    }

    useEffect(() => {
        if (!userFunctions || didLoad) return
        setIsLoading(true)
        loadData().catch(() => {})
        setIsLoading(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userFunctions])

    function renderMeta(llPeriod: IPeriodCache) {
        return <ReportMeta llPeriod={llPeriod} />
    }

    function renderContent() {
        const [view, viewId] = selectedKey.split(';;;')
        switch (view) {
            case 'taxYear':
                return (
                    <TaxYearOverview
                        taxYear={taxYears.find((ty) => ty.id === viewId)}
                        getTaxYearData={getTaxYearData}
                        loadOverviewData={loadData}
                        switchToTab={setSelectedKey}
                        apiFunctions={userFunctions}
                        userData={userData}
                        resetTaxYearData={resetTaxYearData}
                    />
                )
            case 'taxYearReport':
                const [taxYearId, reportId] = viewId.split(':::')
                return (
                    <div key={new Date().valueOf()}>
                        <ReportView
                            taxYear={taxYears.find((ty) => ty.id === taxYearId)}
                            reportId={reportId}
                            getTaxYearData={getTaxYearData}
                            loadOverviewData={loadData}
                            switchToTab={setSelectedKey}
                            renderMeta={renderMeta}
                            apiFunctions={userFunctions}
                            refreshReportDatesData={refreshReportDatesData}
                            isRefreshing={isRefreshing}
                        />
                    </div>
                )
        }
    }

    switch (contentType) {
        case ComplianceReportsTypes.LLUser:
            return (
                <LLUserComplianceReports
                    getTaxYearData={getTaxYearData}
                    loadOverviewData={loadData}
                    renderTaxYearContent={renderContent}
                    selectedKey={selectedKey}
                    setSelectedKey={setSelectedKey}
                    taxYears={taxYears}
                    isLoading={isLoading}
                    userData={userData}
                    isBackgroundLoading={isBackgroundLoading}
                />
            )
        case ComplianceReportsTypes.Revisor:
            return (
                <RevisorNav
                    getTaxYearData={getTaxYearData}
                    loadOverviewData={loadData}
                    renderTaxYearContent={renderContent}
                    selectedKey={selectedKey}
                    setSelectedKey={setSelectedKey}
                    taxYears={taxYears}
                    isLoading={isLoading}
                    userData={userData}
                    isBackgroundLoading={isBackgroundLoading}
                />
            )
    }
}
